import { useEffect, useState } from 'react'
import { MinusCircleIcon, PlusIcon } from '@heroicons/react/24/outline'
import { Modal, Button, Form, FormListFieldData, Input, Select } from 'antd'
import { LeadFormItem, LeadFormItemType } from '../forms/Editor/extensions/LeadForm/LeadForm'

interface EditorLeadFormModalValues {
  formItems: LeadFormItem[],
  ctaButtonText: string
}

interface EditorLeadFormModalProps {
  defaultValues: EditorLeadFormModalValues
  open: boolean,
  onOk: (values: EditorLeadFormModalValues) => void,
  onCancel: () => void
}


const formItemLayoutWithOutLabel = {
  wrapperCol: {
    xs: { span: 24, offset: 0 },
    sm: { span: 20, offset: 4 },
  },
};

const defaultValues = {
  formItems: [
    {label: 'First name', type: LeadFormItemType.FirstName},
    {label: 'Email', type: LeadFormItemType.Email}
  ],
  ctaButtonText: 'Submit'
}

const options = [
  {value: LeadFormItemType.FirstName, label:'First name'},
  {value: LeadFormItemType.LastName, label:'Last name'},
  {value: LeadFormItemType.Email, label:'Email'},
  {value: LeadFormItemType.Phone, label:'Phone'},
  {value: LeadFormItemType.Text, label:'Text'}
]

const LeadFormItemInput = ({field, fields, remove} : {field: FormListFieldData, fields: FormListFieldData[], remove: (index: number) => void}) => {
  return (
    <div className="flex gap-x-3">
      <Form.Item
        {...field}
        label='Label'
        className="flex-auto"
        name={[field.name, 'label']}
        rules={[{ required: true }]}
        >
          <Input size="large" className='max-w-lg shadow-sm sm:max-w-xs sm:text-sm border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 rounded-md' />
        </Form.Item>
        <Form.Item
          {...field}
          label="Type"
          className="flex-none w-36"
          name={[field.name, 'type']}
          rules={[{ required: true }]}
        >
          <Select size='large' options={options} placeholder='Select a type'/>
        </Form.Item>
      {fields.length > 1 ? (
        <MinusCircleIcon
          className="dynamic-delete-button w-6 inline-block cursor-pointer"
          onClick={() => remove(field.name)}
        />
      ) : null
                  }
  </div>
  )
}
export default function EditorLeadFormModal(props: EditorLeadFormModalProps) {
  const editMode = props.defaultValues !== undefined
  const [form] = Form.useForm()

  const handleOk = () => {
    const values = form.getFieldsValue()
    props.onOk(values)
    form.resetFields()
    props.onCancel()
  }
  const handleCancel = () => {
    form.resetFields()
    props.onCancel()
  }

  useEffect(() => {
    form.setFieldsValue(props.defaultValues)
  }, [form, props.defaultValues, props.open])

  const [disabled, setDisabled] = useState(false);

  const values = Form.useWatch([], form);

  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setDisabled(false);
      },
      () => {
        setDisabled(true);
      },
    );
  }, [values, form]);

  return (
    <Modal
      {...props}
      onOk={handleOk}
      onCancel={handleCancel}
      title={editMode ? 'Edit Lead Form' : 'Add Lead Form'}
      okText={editMode ? 'Edit Lead Form' : 'Add Lead Form'}
      className='w-[526px]'
      okButtonProps={{disabled}}
    >
      <Form
        name="dynamic_form_item p-5 w-20"
        layout='vertical'
        form={form}
        initialValues={props.defaultValues || defaultValues}
        requiredMark={false}
      >
        <Form.List
          name="formItems"
        >
          {(fields, { add, remove }, { errors }) => (
            <>
              {fields.map((field, index) => (
                <Form.Item
                  noStyle
                  required={false}
                  key={field.key}
                >
                  <LeadFormItemInput field={field} fields={fields} remove={remove}/>
                </Form.Item>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusIcon className='w-5 inline-block' />}
                >
                  Add form item
                </Button>
                <Form.ErrorList errors={errors} />
              </Form.Item>
            </>
          )}
        </Form.List>
        <Form.Item
          label="Consent text"
          name="consentText"
        >
          <Input className='max-w-lg w-full shadow-sm sm:text-sm border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 rounded-md' />
        </Form.Item>
        <Form.Item
          label="Call to action button text"
          name="ctaButtonText"
        >
          <Input className='w-full shadow-sm sm:text-sm border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 rounded-md' placeholder="Submit" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
